/**
 * Request-signing glue for direct (browser-signed) auth. When the feature flag
 * is on, every outbound request is signed in-browser with the unlocked
 * identity and sent straight to the agent (no gateway). See this.i wqz7k3mnbp.
 */

import type {FetchArgs} from '@reduxjs/toolkit/query/react';

import constants from '@/config/constant';
import {getIdentity} from './session';
import {applyAgentTarget} from './agent-target';
import {signRequest, type HttpSigHeaders} from './httpsig';

/** True when the app should sign and call the agent directly. */
export function isDirectAuth(): boolean {
  return constants.DIRECT_AUTH_ENABLED;
}

/** Thrown when a direct-auth request is attempted with no unlocked session. */
export class SessionLockedError extends Error {
  constructor() {
    super('Direct-auth session is locked');
    this.name = 'SessionLockedError';
  }
}

function headersToRecord(headers: FetchArgs['headers']): HttpSigHeaders {
  if (!headers) return {};
  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries()) as HttpSigHeaders;
  }
  if (Array.isArray(headers)) return Object.fromEntries(headers) as HttpSigHeaders;
  return {...(headers as Record<string, string>)};
}

/** Serialize a request body the same way fetchBaseQuery will, for digesting. */
function serializeBody(body: unknown): string {
  if (body == null) return '';
  if (typeof body === 'string') return body;
  return JSON.stringify(body);
}

/**
 * Sign a request with the unlocked identity. Throws SessionLockedError if no
 * session is active.
 */
export async function signDirectRequest(opts: {
  method: string;
  url: string;
  body?: string | Uint8Array;
  headers?: HttpSigHeaders;
}): Promise<HttpSigHeaders> {
  const identity = getIdentity();
  if (!identity) throw new SessionLockedError();
  return signRequest({identity, ...opts});
}

/**
 * Return RTK Query args with RFC 9421 signature headers added when direct auth
 * is on; a no-op (returns the same args) otherwise. Throws SessionLockedError
 * when direct auth is on but no session is unlocked.
 */
export async function signFetchArgs(
  args: string | FetchArgs,
): Promise<string | FetchArgs> {
  if (!isDirectAuth()) return args;

  const fetchArgs: FetchArgs = typeof args === 'string' ? {url: args} : {...args};
  // Re-point the request at the session's chosen agent before signing.
  fetchArgs.url = applyAgentTarget(fetchArgs.url);
  const method = (fetchArgs.method ?? 'GET').toString().toUpperCase();
  const signed = await signDirectRequest({
    method,
    url: fetchArgs.url,
    body: serializeBody(fetchArgs.body),
    headers: headersToRecord(fetchArgs.headers),
  });
  fetchArgs.headers = signed;
  return fetchArgs;
}
